import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Box, CircularProgress, Typography } from '../utils/mui-imports';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const PinterestPinCarousel = ({ onPinSelect }) => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const fetchPins = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch pins from the API endpoint
        const response = await fetch('/api/pinterest/board-pins?limit=10');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setPins(result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch pins');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching pins:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPins();
  }, []);

  const handlePinClick = (pin) => {
    if (onPinSelect) {
      onPinSelect(pin.pinUrl);
    }
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={40} sx={{ color: '#E60023' }} />
        <Typography variant="body2" color="text.secondary">
          Loading recent pins...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="body2" color="error">
          Failed to load pins: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <style>
        {`
          .swiper-button-next,
          .swiper-button-prev {
            background: rgba(255, 255, 255, 0.9) !important;
            border-radius: 50% !important;
            width: 40px !important;
            height: 40px !important;
            margin-top: -20px !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          }
          .swiper-button-next:after,
          .swiper-button-prev:after {
            font-size: 16px !important;
            color: #E60023 !important;
            font-weight: bold !important;
          }
          .swiper-pagination-bullet {
            background: #E60023 !important;
            opacity: 0.5 !important;
          }
          .swiper-pagination-bullet-active {
            opacity: 1 !important;
          }
        `}
      </style>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={isMobile ? 5 : 10}
        slidesPerView={isMobile ? 2 : 3}
        navigation={true}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        style={{
          '--swiper-navigation-color': '#E60023',
          '--swiper-pagination-color': '#E60023',
          '--swiper-navigation-size': '24px',
          '--swiper-navigation-bg-color': 'rgba(255, 255, 255, 0.9)',
          '--swiper-navigation-border-radius': '50%',
          padding: '10px 0 40px 0'
        }}
      >
        {pins.map((pin) => (
          <SwiperSlide key={pin.id}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
              onClick={() => handlePinClick(pin)}
            >
              {/* Title Overlay with Glassmorphism Effect */}
              <Box 
                sx={{ 
                  position: 'absolute', 
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 1,
                  backgroundColor: 'rgba(1,1,1,0.5)',
                  backdropFilter: 'blur(5px)',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'left',
                  zIndex: 1
                }}
              >
                <Typography
                  variant="body2"
                  color="white"
                  sx={{ 
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%'
                  }}
                >
                  {pin.title || "Pin"}
                </Typography>
              </Box>
              
              <img
                src={pin.imageUrl}
                alt={pin.title}
                style={{
                  width: '100%',
                  height: '250px',
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x250/E60023/FFFFFF?text=Pin+Image';
                }}
              />
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default PinterestPinCarousel;
